import React from 'react';
import { Alert, Button, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { IVerbaScholaAiMessage, IVerbaScholaAiPreview } from '../api/VerbaScholaLessonAiApi';

type AiFeedback = IVerbaScholaAiMessage['feedback'];
/** Saves a rating; resolves null once saved, or why it was not. */
export type RateResponse = (rating: 'positive' | 'negative', reason?: string, note?: string) => Promise<string | null>;

/** What the copilot offers a page review. */
export interface ReviewChat {
    /** Aims the copilot's next message at the proposal under review. */
    refine: (preview: IVerbaScholaAiPreview) => void;
    disabled: boolean;
}

const feedbackReasons = [
    ['wrong_level', 'Wrong level'], ['inaccurate_chinese', 'Inaccurate Chinese'],
    ['too_long', 'Too long'], ['off_target', 'Off target'], ['other', 'Other'],
] as const;

export function FeedbackReasonField({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }) {
    return <TextField select size="small" label={label} value={value} onChange={event => onChange(event.target.value)}>
        <MenuItem value="">No reason</MenuItem>
        {feedbackReasons.map(([reason, text]) => <MenuItem key={reason} value={reason}>{text}</MenuItem>)}
    </TextField>;
}

/** An opened form can start below the visible part of the history, so it brings itself into view; one redrawn open stays put. */
export function useRevealWhenOpen(open: boolean) {
    const form = React.useRef<HTMLDivElement | null>(null);
    const wasOpen = React.useRef(open);
    React.useEffect(() => {
        if (open && !wasOpen.current) form.current?.scrollIntoView({ block: 'nearest' });
        wasOpen.current = open;
    }, [open]);
    return form;
}

/** A rating being written, and the last one this form saved. ``seen``: the saved rating it last read, to tell a newer one. */
export interface FeedbackDraft {
    open: boolean; explaining: boolean; reason: string; note: string; sending: boolean; failure: string | null; sent: AiFeedback; seen: string | null;
}
export const EMPTY_FEEDBACK_DRAFT: FeedbackDraft = { open: false, explaining: false, reason: '', note: '', sending: false, failure: null, sent: null, seen: null };

/** Optional feedback on an AI response, closed to one link until the teacher asks for it. ``draftState`` keeps the form
 * outside it, for a place that redraws it: a review's versions redraw its bar. */
export function MessageFeedback({ feedback, disabled, onSubmit, label = 'Give feedback', draftState }: {
    feedback?: AiFeedback; disabled: boolean; onSubmit: RateResponse; label?: string;
    draftState?: [FeedbackDraft, (value: FeedbackDraft | ((current: FeedbackDraft) => FeedbackDraft)) => void];
}) {
    const ownDraft = React.useState(EMPTY_FEEDBACK_DRAFT);
    const [draft, setDraft] = draftState ?? ownDraft;
    const { open, explaining, reason, note, sending, failure, sent, seen } = draft;
    const change = (fields: Partial<FeedbackDraft>) => setDraft(current => ({ ...current, ...fields }));
    const form = useRevealWhenOpen(open);
    // A saved rating that arrives after this form's is newer, perhaps from another tab, and replaces it.
    const saved = JSON.stringify(feedback ?? null);
    React.useEffect(() => {
        if (seen !== saved) change(seen === null ? { seen: saved } : { seen: saved, sent: null });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [saved, seen]);
    const shown = sent ?? feedback;
    const close = () => change({ open: false, explaining: false, failure: null });
    // Changing a saved rating starts from it: a new one replaces the old, reason and note included.
    const show = () => change({ open: true, reason: shown?.reason_code ?? '', note: shown?.note ?? '', explaining: shown?.rating === 'negative' });
    // The form stays open until the rating is saved, and a failure is told here: the copilot may be closed.
    const submit = async (rating: 'positive' | 'negative', reasonCode?: string, text?: string) => {
        change({ sending: true, failure: null });
        const problem = await onSubmit(rating, reasonCode, text);
        change(problem ? { sending: false, failure: problem }
            : { sending: false, open: false, explaining: false, failure: null, sent: { rating, reason_code: reasonCode ?? null, note: text ?? null } });
    };
    return <Stack spacing={0.5} sx={{ mt: 1 }}>
        {shown && <Typography variant="caption" color="text.secondary">Feedback saved: {shown.rating === 'positive' ? 'Helpful' : 'Needs work'}{shown.reason_code ? ` · ${feedbackReasons.find(([value]) => value === shown.reason_code)?.[1] ?? shown.reason_code}` : ''}</Typography>}
        {!open ? <Button size="small" sx={{ alignSelf: 'flex-start' }} onClick={show}>{shown ? 'Change feedback' : label}</Button>
            : <Stack ref={form} spacing={0.5}>
                <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
                    <Typography variant="caption">Was this response useful?</Typography>
                    <Button size="small" variant={shown?.rating === 'positive' ? 'contained' : 'text'} disabled={disabled || sending}
                        onClick={() => void submit('positive')}>Helpful</Button>
                    <Button size="small" variant={shown?.rating === 'negative' ? 'contained' : 'text'} disabled={disabled || sending}
                        aria-expanded={explaining} onClick={() => change({ explaining: true })}>Needs work</Button>
                </Stack>
                {explaining && <>
                    <FeedbackReasonField label="Reason (optional)" value={reason} onChange={value => change({ reason: value })} />
                    <TextField size="small" label="Note (optional, up to 500 characters)" value={note} inputProps={{ maxLength: 500 }}
                        onChange={event => change({ note: event.target.value })} />
                </>}
                {failure && <Alert severity="error">{failure}</Alert>}
                <Stack direction="row" spacing={0.5}>
                    {explaining && <Button size="small" disabled={disabled || sending} onClick={() => void submit('negative', reason || undefined, note.trim() || undefined)}>Send feedback</Button>}
                    <Button size="small" disabled={sending} onClick={close}>Cancel</Button>
                </Stack>
            </Stack>}
    </Stack>;
}
